/**
 * Admin → Equipments (geladeiras / freezers).
 *
 * O metadata jsonb guarda setpoint_min/max e marca/modelo.  Esses
 * campos têm UI dedicada; demais ficam no JSON livre.
 */

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Building2, LayoutGrid, Pencil, Plus, Rows3, Trash2 } from "lucide-react";
import toast from "react-hot-toast";
import clsx from "clsx";

import { equipmentsApi } from "@/api/equipments";
import { apiErrorMessage } from "@/api/client";
import { qk, useDevices, useEquipmentKinds, useEquipments, useSites, useTenants } from "@/hooks/queries";
import { useRotuloDeUnidade } from "@/hooks/useRotuloDeUnidade";
import { useAuth } from "@/contexts/AuthContext";
import type { Equipment, EquipmentCreate, EquipmentKind, EquipmentUpdate, Site } from "@/types";

import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/Button";
import { Card, CardBody } from "@/components/Card";
import { DataTable, type Column } from "@/components/DataTable";
import { EquipmentAdminCard } from "@/components/EquipmentAdminCard";
import { Modal } from "@/components/Modal";
import { Field, Input, Select } from "@/components/Field";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Badge } from "@/components/Badge";
import { SitesSidebar, type SelectedSiteNode } from "@/components/SitesSidebar";
import { Tip } from "@/components/Tip";
import { shortId } from "@/lib/format";

type ViewMode = "tree" | "table";
const VIEW_MODE_KEY = "envpulse:equipments-view-mode";

function loadViewMode(): ViewMode {
  try {
    const raw = localStorage.getItem(VIEW_MODE_KEY);
    return raw === "table" ? "table" : "tree";
  } catch {
    return "tree";
  }
}

// A lista de tipos NÃO mora mais aqui. Ela vem de GET /equipment-kinds, com
// as faixas sugeridas e o vocabulário de cada tipo. Duplicar a lista no
// front foi o que deixou `kind` decorativo por tanto tempo: o campo existia,
// mas nada do backend sabia quais valores ele podia ter.

export function AdminEquipmentsPage() {
  const auth = useAuth();
  const tenants = useTenants(auth.isSuperAdmin);

  // super_admin escolhe um tenant pra filtrar / contextualizar criação.
  // Tenant comum sempre vê só o próprio.
  const [pickedTenantId, setPickedTenantId] = useState<string>("");
  const tenantFilter = auth.isSuperAdmin ? pickedTenantId || undefined : undefined;

  const [showArchived, setShowArchived] = useState(false);
  const equipments = useEquipments({
    ...(tenantFilter ? { tenant_id: tenantFilter } : {}),
    include_archived: showArchived,
  });
  const sites = useSites(tenantFilter ? { tenant_id: tenantFilter } : undefined);
  const { rotulo: rotuloDaUnidade, opcoes: opcoesDeUnidade } = useRotuloDeUnidade();

  // MEMOIZADO, e não é otimização. O formulário tem um efeito de
  // re-inicialização com `siteOptions` nas dependências — ele reseta nome,
  // slug, tipo, setpoints e specs elétricas. Com um array novo a cada
  // render do pai, esse efeito disparava a cada refetch: o react-query
  // refaz as queries ao voltar o foco da janela (`refetchOnWindowFocus`),
  // então bastava alguém alternar de aba pra conferir uma ficha técnica e
  // voltar pra achar o formulário zerado e o tipo de volta em Geladeira.
  const siteOptions = useMemo(
    () => opcoesDeUnidade(sites.data),
    [sites.data, opcoesDeUnidade],
  );
  // Devices só pra contagem por equipment nos cards (não precisa incluir
  // inativos — a contagem "operacional" é o que interessa administrativa).
  const devices = useDevices(tenantFilter ? { tenant_id: tenantFilter } : undefined);
  const qc = useQueryClient();

  const [editTarget, setEditTarget] = useState<Equipment | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Equipment | null>(null);
  const [siteFilter, setSiteFilter] = useState<string>("");
  const [createPrefill, setCreatePrefill] = useState<{ site_id?: string } | null>(null);

  // Modo de visualização — persiste em localStorage
  const [viewMode, setViewMode] = useState<ViewMode>(() => loadViewMode());
  useEffect(() => {
    try { localStorage.setItem(VIEW_MODE_KEY, viewMode); } catch {}
  }, [viewMode]);

  // Nó selecionado na sidebar (só no modo tree)
  const [selectedNode, setSelectedNode] = useState<SelectedSiteNode>({ kind: "all" });

  const filtered = useMemo(() => {
    let list = equipments.data ?? [];
    if (viewMode === "tree") {
      if (selectedNode.kind === "site") {
        list = list.filter((e) => e.site_id === selectedNode.id);
      }
    } else {
      if (siteFilter) list = list.filter((e) => e.site_id === siteFilter);
    }
    return list;
  }, [equipments.data, siteFilter, viewMode, selectedNode]);

  // Índice device_count por equipment_id — usado pelos cards
  const deviceCountByEquipment = useMemo(() => {
    const m = new Map<string, number>();
    for (const d of devices.data ?? []) {
      if (d.equipment_id) {
        m.set(d.equipment_id, (m.get(d.equipment_id) ?? 0) + 1);
      }
    }
    return m;
  }, [devices.data]);

  const siteNameById = new Map((sites.data ?? []).map((s) => [s.id, rotuloDaUnidade(s)]));

  const createMut = useMutation({
    mutationFn: (p: EquipmentCreate) => equipmentsApi.create(p),
    onSuccess: () => {
      toast.success("Ativo criado");
      void qc.invalidateQueries({ queryKey: qk.equipments() });
      setIsCreating(false);
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, p }: { id: string; p: EquipmentUpdate }) =>
      equipmentsApi.update(id, p),
    onSuccess: () => {
      toast.success("Ativo atualizado");
      void qc.invalidateQueries({ queryKey: qk.equipments() });
      setEditTarget(null);
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => equipmentsApi.remove(id),
    onSuccess: () => {
      toast.success("Ativo deletado");
      void qc.invalidateQueries({ queryKey: qk.equipments() });
      setDeleteTarget(null);
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const columns: Column<Equipment>[] = [
    {
      key: "name",
      header: "Nome",
      cell: (r) => (
        <span className="flex items-center gap-2">
          <span className={clsx(
            "font-medium",
            r.active
              ? "text-slate-900 dark:text-slate-100"
              : "text-slate-500 dark:text-slate-500 line-through",
          )}>
            {r.name}
          </span>
          {!r.active && (
            <Badge tone="neutral">Arquivada</Badge>
          )}
        </span>
      ),
    },
    { key: "kind", header: "Tipo", cell: (r) => <Badge tone="brand">{r.kind ?? "—"}</Badge> },
    { key: "site", header: "Site", cell: (r) => <span className="text-slate-600 dark:text-slate-400">{siteNameById.get(r.site_id) ?? shortId(r.site_id)}</span>, hideOnMobile: true },
    {
      key: "range",
      header: "Range setpoint",
      hideOnMobile: true,
      cell: (r) => {
        const meta = (r.metadata ?? {}) as Record<string, unknown>;
        const min = meta.setpoint_min;
        const max = meta.setpoint_max;
        if (min == null && max == null) return <span className="text-xs text-slate-400 dark:text-slate-500">não definido</span>;
        return (
          <span className="text-xs text-slate-600 dark:text-slate-400">
            {String(min ?? "—")} a {String(max ?? "—")} °C
          </span>
        );
      },
    },
    { key: "slug", header: "Slug", cell: (r) => <code className="text-xs text-slate-600 dark:text-slate-400">{r.slug}</code>, hideOnMobile: true },
    {
      key: "actions",
      header: "",
      align: "right",
      cell: (r) => (
        <div className="flex justify-end gap-1">
          <Button size="sm" variant="ghost" icon={<Pencil className="h-4 w-4" />} onClick={() => setEditTarget(r)}>
            Editar
          </Button>
          <Button size="sm" variant="ghost" icon={<Trash2 className="h-4 w-4 text-red-500" />} onClick={() => setDeleteTarget(r)} />
        </div>
      ),
    },
  ];

  return (
    <div className="mx-auto max-w-7xl">
      <PageHeader
        title="Cadastro de ativos"
        description="Câmaras, geladeiras, freezers, estufas — qualquer ambiente monitorado. Cada ativo agrupa N sensores."
        actions={
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-md border border-surface-200 dark:border-slate-700 bg-white dark:bg-slate-900">
              <button
                type="button"
                onClick={() => setViewMode("tree")}
                className={clsx(
                  "flex items-center gap-1.5 rounded-l-md px-3 py-1.5 text-xs font-medium",
                  viewMode === "tree"
                    ? "bg-brand-600 text-white"
                    : "text-slate-600 dark:text-slate-300 hover:bg-surface-100 dark:hover:bg-slate-800",
                )}
              >
                <LayoutGrid className="h-3.5 w-3.5" />
                Árvore
              </button>
              <button
                type="button"
                onClick={() => setViewMode("table")}
                className={clsx(
                  "flex items-center gap-1.5 rounded-r-md px-3 py-1.5 text-xs font-medium",
                  viewMode === "table"
                    ? "bg-brand-600 text-white"
                    : "text-slate-600 dark:text-slate-300 hover:bg-surface-100 dark:hover:bg-slate-800",
                )}
              >
                <Rows3 className="h-3.5 w-3.5" />
                Tabela
              </button>
            </div>
            <Button icon={<Plus className="h-4 w-4" />} onClick={() => {
              setCreatePrefill(null);
              setIsCreating(true);
            }}>
              Nova ativo
            </Button>
          </div>
        }
      />

      {/* Seletor de tenant (só super_admin) — filtra sites/equipments/devices */}
      {auth.isSuperAdmin && (
        <Card className="mb-4">
          <CardBody>
            <Field
              label={
                <span className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-brand-600" />
                  Tenant
                </span>
              }
            >
              <Select
                value={pickedTenantId}
                onChange={(e) => {
                  setPickedTenantId(e.target.value);
                  // Ao trocar tenant, volta seleção pra 'Todas' — o nó anterior
                  // pode não existir mais no novo tenant.
                  setSelectedNode({ kind: "all" });
                  setSiteFilter("");
                }}
                options={[
                  { value: "", label: "Todos os tenants" },
                  ...(tenants.data ?? []).map((t) => ({
                    value: t.id,
                    label: `${t.name} (${t.slug})`,
                  })),
                ]}
              />
            </Field>
          </CardBody>
        </Card>
      )}

      {/* Toggle "mostrar arquivadas" — visível nos 2 modos (flag global) */}
      <div className="mb-3 flex justify-end">
        <label className="inline-flex items-center gap-2 rounded-md border border-surface-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-1.5 text-xs">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
            className="h-3.5 w-3.5 rounded border-surface-300 dark:border-slate-700 text-brand-600"
          />
          <span className="text-slate-700 dark:text-slate-300 whitespace-nowrap">
            Mostrar arquivadas
          </span>
        </label>
      </div>

      {viewMode === "table" ? (
        <>
          <div className="mb-4">
            <Field label="Filtrar por site">
              <Select
                value={siteFilter}
                onChange={(e) => setSiteFilter(e.target.value)}
                options={[
                  { value: "", label: "Todos" },
                  ...siteOptions,
                ]}
              />
            </Field>
          </div>

          <DataTable
            data={filtered}
            columns={columns}
            loading={equipments.isLoading}
            rowKey={(r) => r.id}
            emptyTitle="Nenhum ativo cadastrado"
          />
        </>
      ) : (
        <EquipmentsTreeSplit
          sites={sites.data ?? []}
          allEquipments={equipments.data ?? []}
          filteredEquipments={filtered}
          deviceCountByEquipment={deviceCountByEquipment}
          selected={selectedNode}
          onSelect={setSelectedNode}
          onEdit={(eq) => setEditTarget(eq)}
          onDelete={(eq) => setDeleteTarget(eq)}
          onCreate={(prefill) => {
            setCreatePrefill(prefill);
            setIsCreating(true);
          }}
        />
      )}

      <EquipmentFormModal
        key={
          isCreating
            ? `create-${createPrefill?.site_id ?? ""}`
            : `edit-${editTarget?.id ?? "none"}`
        }
        open={isCreating || !!editTarget}
        onClose={() => {
          setIsCreating(false);
          setEditTarget(null);
          setCreatePrefill(null);
        }}
        title={isCreating ? "Nova ativo" : "Editar ativo"}
        initial={editTarget}
        prefill={createPrefill ?? undefined}
        siteOptions={siteOptions}
        submitting={createMut.isPending || updateMut.isPending}
        onSubmit={(payload) => {
          if (editTarget) {
            // No PATCH não enviamos site_id (não permitido movimentar de site).
            const { site_id: _siteId, ...rest } = payload;
            updateMut.mutate({ id: editTarget.id, p: rest });
          } else {
            createMut.mutate(payload);
          }
        }}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        title="Deletar ativo?"
        message={
          deleteTarget
            ? `Devices vinculados à '${deleteTarget.name}' ficam órfãos (equipment_id NULL).`
            : ""
        }
        confirmLabel="Deletar"
        loading={deleteMut.isPending}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteMut.mutate(deleteTarget.id)}
      />
    </div>
  );
}

// ---------- form modal ------------------------------------------------------

function EquipmentFormModal({
  open,
  onClose,
  title,
  initial,
  prefill,
  siteOptions,
  submitting,
  onSubmit,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  initial?: Equipment | null;
  /** Prefill contextual pro create (ex.: 'nova ativo em Site X'). */
  prefill?: { site_id?: string } | null;
  siteOptions: { value: string; label: string }[];
  submitting?: boolean;
  onSubmit: (p: EquipmentCreate) => void;
}) {
  const [siteId, setSiteId] = useState(initial?.site_id ?? prefill?.site_id ?? siteOptions[0]?.value ?? "");
  const [slug, setSlug] = useState(initial?.slug ?? "");
  const [name, setName] = useState(initial?.name ?? "");
  const [kind, setKind] = useState(initial?.kind ?? "fridge");
  const kinds = useEquipmentKinds();
  const kindOptions = useMemo(() => {
    const lista = kinds.data ?? [];
    // Agrupa mantendo a ordem que o backend mandou — Refrigeração,
    // Aquecimento, Ambiente, Outros.
    const grupos: { grupo: string; itens: EquipmentKind[] }[] = [];
    for (const k of lista) {
      const atual = grupos.find((g) => g.grupo === k.grupo);
      if (atual) atual.itens.push(k);
      else grupos.push({ grupo: k.grupo, itens: [k] });
    }
    return grupos;
  }, [kinds.data]);
  const tipoAtual = (kinds.data ?? []).find((k) => k.slug === kind) ?? null;
  const initialMeta = (initial?.metadata ?? {}) as Record<string, unknown>;
  const [setpointMin, setSetpointMin] = useState<string>(
    initialMeta.setpoint_min != null ? String(initialMeta.setpoint_min) : "",
  );
  const [setpointMax, setSetpointMax] = useState<string>(
    initialMeta.setpoint_max != null ? String(initialMeta.setpoint_max) : "",
  );
  // Disponibilidade esperada deste ativo. Morava no cabeçalho da aba de
  // limites, o que misturava "o que é este ativo" com "quando avisar sobre
  // ele". É atributo de cadastro, como o setpoint.
  const [sloTarget, setSloTarget] = useState<string>(
    initialMeta.slo_target_pct != null ? String(initialMeta.slo_target_pct) : "",
  );
  const [manufacturer, setManufacturer] = useState<string>(
    String(initialMeta.manufacturer ?? ""),
  );
  const [expectedTempSensors, setExpectedTempSensors] = useState<string>(
    initialMeta.expected_temp_sensors != null ? String(initialMeta.expected_temp_sensors) : "3",
  );
  // Specs elétricas — strings pra suportar campo vazio no input. Convertemos
  // pra number só no submit. Vazio = "não declarado" (NULL no backend).
  const [nominalVoltage, setNominalVoltage] = useState<string>(
    initial?.nominal_voltage_v != null ? String(initial.nominal_voltage_v) : "",
  );
  const [nominalPower, setNominalPower] = useState<string>(
    initial?.nominal_power_w != null ? String(initial.nominal_power_w) : "",
  );
  const [phase, setPhase] = useState<"mono" | "bi" | "tri">(initial?.phase ?? "mono");
  const [active, setActive] = useState<boolean>(initial?.active ?? true);

  const [tocado, setTocado] = useState({ min: false, max: false, sensores: false });

  useEffect(() => {
    setSiteId(initial?.site_id ?? prefill?.site_id ?? siteOptions[0]?.value ?? "");
    setSlug(initial?.slug ?? "");
    setName(initial?.name ?? "");
    setKind(initial?.kind ?? "fridge");
    const meta = (initial?.metadata ?? {}) as Record<string, unknown>;
    setSetpointMin(meta.setpoint_min != null ? String(meta.setpoint_min) : "");
    setSetpointMax(meta.setpoint_max != null ? String(meta.setpoint_max) : "");
    setSloTarget(meta.slo_target_pct != null ? String(meta.slo_target_pct) : "");
    setManufacturer(String(meta.manufacturer ?? ""));
    setExpectedTempSensors(meta.expected_temp_sensors != null ? String(meta.expected_temp_sensors) : "3");
    setNominalVoltage(initial?.nominal_voltage_v != null ? String(initial.nominal_voltage_v) : "");
    setNominalPower(initial?.nominal_power_w != null ? String(initial.nominal_power_w) : "");
    setPhase(initial?.phase ?? "mono");
    setActive(initial?.active ?? true);
    // O modal não desmonta entre um cadastro e outro, então `tocado`
    // sobrevive. Sem zerar aqui, quem digitasse um setpoint na primeira
    // ativo nunca mais veria preset nenhum nas seguintes — e o sintoma
    // seria idêntico ao bug que a gente acabou de corrigir.
    setTocado({ min: false, max: false, sensores: false });
  }, [initial, prefill, siteOptions]);

  /**
   * Campos que a PESSOA mexeu. Sem isso não dá pra distinguir "2.0 porque
   * eu digitei" de "2.0 porque o preset anterior deixou aí".
   *
   * A primeira versão desta tela só preenchia campo vazio, na intenção de
   * não pisar em ajuste manual. O efeito prático foi o oposto: trocar de
   * Geladeira para Balcão térmico mantinha 0/5 no formulário embaixo de um
   * texto citando a norma dos 60 °C. O valor parecia aleatório porque era
   * resíduo do tipo anterior.
   */

  /**
   * Aplica a sugestão do tipo nos campos ainda não tocados.
   *
   * Campo não tocado SEGUE o tipo, inclusive esvaziando quando o tipo não
   * tem faixa (estufa, sala) — deixar −25/−18 sob o rótulo "Estufa" seria a
   * mesma contradição de antes, só que ao contrário.
   *
   * Campo tocado nunca é mexido sozinho. Preset que passa por cima de
   * calibração manual é a forma mais rápida de alguém perder um ajuste que
   * custou uma visita técnica; para esses existe o botão explícito abaixo.
   */
  function aplicarSugestao(forcar = false) {
    const preset = (kinds.data ?? []).find((k) => k.slug === kind);
    if (!preset) return;
    const texto = (v: number | null) => (v == null ? "" : String(v));
    if (forcar || !tocado.min)      setSetpointMin(texto(preset.setpoint_min));
    if (forcar || !tocado.max)      setSetpointMax(texto(preset.setpoint_max));
    if (forcar || !tocado.sensores) {
      setExpectedTempSensors(
        preset.expected_temp_sensors != null ? String(preset.expected_temp_sensors) : "",
      );
    }
    if (forcar) setTocado({ min: false, max: false, sensores: false });
  }

  /** Os campos hoje batem com o que o tipo sugere? Decide o botão abaixo. */
  const confereComOTipo = (() => {
    const preset = (kinds.data ?? []).find((k) => k.slug === kind);
    if (!preset) return true;
    const texto = (v: number | null) => (v == null ? "" : String(v));
    return (
      setpointMin === texto(preset.setpoint_min) &&
      setpointMax === texto(preset.setpoint_max) &&
      expectedTempSensors ===
        (preset.expected_temp_sensors != null ? String(preset.expected_temp_sensors) : "")
    );
  })();

  /**
   * Em CRIAÇÃO a sugestão entra sozinha a cada troca de tipo. Em EDIÇÃO não:
   * ali os valores são dados salvos do ativo, e reescrever config real sem
   * pedir é pior que dar trabalho. O botão "usar a sugestão" cobre esse caso.
   */
  useEffect(() => {
    if (initial) return;
    aplicarSugestao();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, kinds.data, initial]);

  // Corrente nominal derivada em tempo real (mostrada como hint na UI).
  // Mesma fórmula do backend (rules.py / models.py):
  //   mono e bi → I = P/V (bi tem a mesma fórmula porque V é a tensão de
  //                        operação do equipamento, ex: 220V em rede 127V).
  //   tri       → I = P/(V·√3).
  const derivedCurrent = (() => {
    const v = parseInt(nominalVoltage, 10);
    const w = parseInt(nominalPower, 10);
    if (!v || !w) return null;
    const i = phase === "tri" ? w / (v * Math.sqrt(3)) : w / v;
    return Math.round(i * 100) / 100;
  })();

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const meta: Record<string, unknown> = {};
    if (setpointMin) meta.setpoint_min = parseFloat(setpointMin);
    if (setpointMax) meta.setpoint_max = parseFloat(setpointMax);
    if (manufacturer) meta.manufacturer = manufacturer;
    const slo = parseFloat(sloTarget);
    if (!isNaN(slo) && slo >= 0 && slo <= 100) meta.slo_target_pct = slo;
    const n = parseInt(expectedTempSensors, 10);
    if (!isNaN(n) && n >= 0 && n <= 12) meta.expected_temp_sensors = n;
    // Preserva chaves desconhecidas que existiam no initial.
    const KNOWN = new Set(["setpoint_min", "setpoint_max", "manufacturer",
                           "expected_temp_sensors", "slo_target_pct"]);
    const original = (initial?.metadata ?? {}) as Record<string, unknown>;
    for (const [k, v] of Object.entries(original)) {
      if (!KNOWN.has(k)) {
        meta[k] = v;
      }
    }

    const voltageNum = parseInt(nominalVoltage, 10);
    const powerNum = parseInt(nominalPower, 10);

    onSubmit({
      site_id: siteId,
      slug,
      name,
      kind,
      metadata: meta,
      nominal_voltage_v: isNaN(voltageNum) ? null : voltageNum,
      nominal_power_w:   isNaN(powerNum)   ? null : powerNum,
      phase,
      active,
    });
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button type="submit" form="equipment-form" loading={submitting}>
            Salvar
          </Button>
        </>
      }
    >
      <form id="equipment-form" onSubmit={handleSubmit} className="space-y-4">
        {!initial && (
          <Field label="Site" required>
            <Select
              value={siteId}
              onChange={(e) => setSiteId(e.target.value)}
              required
              options={siteOptions}
              placeholder="Selecione o site"
            />
          </Field>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Nome" required>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder={tipoAtual?.exemplo_nome ?? "Ativo 01"}
            />
          </Field>
          <Field label="Tipo" required>
            {/* <select> nativo com optgroup: o componente Select recebe uma
                lista plana, e agrupar por Refrigeração / Aquecimento /
                Ambiente é o que faz nove opções continuarem legíveis. */}
            <select
              className="input"
              value={kind}
              onChange={(e) => setKind(e.target.value)}
              required
            >
              {kindOptions.map((g) => (
                <optgroup key={g.grupo} label={g.grupo}>
                  {g.itens.map((k) => (
                    <option key={k.slug} value={k.slug}>{k.label}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </Field>
        </div>

        <Field
          label="Slug"
          required
        >
          <Input
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            required
            pattern="[a-zA-Z0-9_-]+"
            placeholder={tipoAtual?.exemplo_slug ?? "unidade_01"}
          />
        </Field>

        <div className="rounded-lg border border-surface-200 dark:border-slate-800 bg-surface-50/50 dark:bg-slate-900/50 p-3">
          <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Range de setpoint (°C)
          </div>
          {/* A norma fica à vista de quem está cadastrando. Sem isso o
              preset é um número mágico, e ninguém sabe se pode mexer. */}
          {tipoAtual?.referencia && (
            <p className="mb-3 text-xs text-slate-500 dark:text-slate-400">
              Sugerido por: {tipoAtual.referencia}. Ajuste se o seu caso pedir.
            </p>
          )}
          {/* Só aparece quando os campos NÃO refletem mais a sugestão do
              tipo — em edição, ou depois de alguém digitar. Evita a
              contradição de mostrar uma norma e um valor que não é dela,
              sem reescrever dado salvo por conta própria. */}
          {tipoAtual && !confereComOTipo && (
            <button
              type="button"
              onClick={() => aplicarSugestao(true)}
              className="mb-3 text-xs font-medium text-brand-600 underline
                         underline-offset-2 hover:text-brand-700
                         dark:text-brand-400 dark:hover:text-brand-300"
            >
              Usar a sugestão de {tipoAtual.label}
              {tipoAtual.setpoint_min != null || tipoAtual.setpoint_max != null
                ? ` (${tipoAtual.setpoint_min ?? "—"} a ${tipoAtual.setpoint_max ?? "—"} °C)`
                : ""}
            </button>
          )}
          {tipoAtual && tipoAtual.setpoint_min == null && tipoAtual.setpoint_max == null && (
            <p className="mb-3 text-xs text-slate-500 dark:text-slate-400">
              Este tipo não tem faixa padrão de mercado — preencha conforme o
              equipamento.
            </p>
          )}
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Mínimo">
              <Input
                type="number"
                step="0.1"
                value={setpointMin}
                onChange={(e) => {
                  setSetpointMin(e.target.value);
                  setTocado((t) => ({ ...t, min: true }));
                }}
                placeholder={tipoAtual?.setpoint_min != null ? String(tipoAtual.setpoint_min) : "—"}
              />
            </Field>
            <Field label="Máximo">
              <Input
                type="number"
                step="0.1"
                value={setpointMax}
                onChange={(e) => {
                  setSetpointMax(e.target.value);
                  setTocado((t) => ({ ...t, max: true }));
                }}
                placeholder={tipoAtual?.setpoint_max != null ? String(tipoAtual.setpoint_max) : "—"}
              />
            </Field>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Fabricante">
            <Input value={manufacturer} onChange={(e) => setManufacturer(e.target.value)} placeholder="Consul" />
          </Field>
          <Field
            label={
              <>
                Sensores de temperatura esperados
                <Tip label="Sensores de temperatura esperados">
                  Quantos sensores de temperatura este ativo deve ter. Serve
                  pra detectar sensor faltando.
                  {tipoAtual?.expected_temp_sensors != null && (
                    <>
                      {" "}Para {tipoAtual.label.toLowerCase()} o usual são{" "}
                      {tipoAtual.expected_temp_sensors}.
                    </>
                  )}
                </Tip>
              </>
            }
          >
            <Input
              type="number"
              min="0"
              max="12"
              step="1"
              value={expectedTempSensors}
              onChange={(e) => {
                setExpectedTempSensors(e.target.value);
                setTocado((t) => ({ ...t, sensores: true }));
              }}
              placeholder="3"
            />
          </Field>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label={
              <>
                SLO alvo (%)
                <Tip label="SLO alvo">
                  Quanto do tempo se espera que este ativo esteja dentro da
                  faixa e reportando. É o alvo contra o qual o relatório
                  compara o período. Em branco, vale 99%.
                </Tip>
              </>
            }
          >
            <Input
              type="number"
              min="0"
              max="100"
              step="0.1"
              value={sloTarget}
              onChange={(e) => setSloTarget(e.target.value)}
              placeholder="99"
            />
          </Field>
        </div>

        {/* Specs elétricas — opcional. Quando preenchido, o backend cria
            auto-rules de power_overload (>130% nominal) e current_spike
            (>200% nominal) específicas pra esse ativo. */}
        <div className="rounded-lg border border-surface-200 dark:border-slate-800 bg-surface-50/50 dark:bg-slate-900/50 p-3">
          <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Especificações elétricas
          </div>
          <div className="mb-3 text-xs text-slate-500 dark:text-slate-400">
            Opcional. Preencher ativa alertas automáticos de sobrecarga
            (compressor trabalhando demais) e pico de corrente (defeito).
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Tensão (V)">
              <Select
                value={nominalVoltage}
                onChange={(e) => setNominalVoltage(e.target.value)}
                options={[
                  { value: "",    label: "— não declarada —" },
                  { value: "110", label: "110 V" },
                  { value: "127", label: "127 V" },
                  { value: "220", label: "220 V" },
                  { value: "240", label: "240 V" },
                  { value: "380", label: "380 V" },
                  { value: "440", label: "440 V" },
                ]}
              />
            </Field>
            <Field
              label={
                <>
                  Potência nominal (W)
                  <Tip label="Potência nominal (W)">
                    Referência: geladeira comercial ~200 W, freezer ~350 W. Vem na
                etiqueta do equipamento.
                  </Tip>
                </>
              }
            >
              <Input
                type="number"
                min="1"
                max="100000"
                step="1"
                value={nominalPower}
                onChange={(e) => setNominalPower(e.target.value)}
                placeholder="350"
              />
            </Field>
            <Field
              label={
              <>
                Fase
                <Tip label="Fase">
                  No Brasil, bifásico (F+F+N) é o mais comum em 220V; monofásico em 127V.
                </Tip>
              </>
            }
            >
              <Select
                value={phase}
                onChange={(e) => setPhase(e.target.value as "mono" | "bi" | "tri")}
                options={[
                  { value: "mono", label: "Monofásico (1F+N)" },
                  { value: "bi",   label: "Bifásico (2F+N)" },
                  { value: "tri",  label: "Trifásico (3F)" },
                ]}
              />
            </Field>
          </div>
          {derivedCurrent !== null && (
            <div className="mt-3 rounded-md bg-blue-50 px-3 py-2 text-xs text-blue-900 dark:bg-blue-950/40 dark:text-blue-200">
              Corrente nominal calculada: <strong>{derivedCurrent.toFixed(2)} A</strong>{" "}
              <span className="opacity-70">
                ({phase === "tri" ? "P / (V·√3) — trifásico" : "P / V"})
              </span>
              <br />
              Ao salvar, serão criadas automaticamente 2 regras de alerta:
              <span className="ml-1 font-medium">power_overload</span> (&gt;
              {Math.round(parseInt(nominalPower, 10) * 1.3)} W sustentado por 10 min) e{" "}
              <span className="font-medium">current_spike</span> (&gt;
              {(derivedCurrent * 2).toFixed(2)} A).
            </div>
          )}
        </div>

        {/* Toggle Ativa — só aparece em edição. Cadastro novo sempre entra ativo. */}
        {initial && (
          <div className="rounded-md border border-surface-200 dark:border-slate-800 bg-surface-50/50 dark:bg-slate-800/40 p-3">
            <label className="flex items-start gap-3">
              <input
                type="checkbox"
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-surface-300 dark:border-slate-700 text-brand-600"
              />
              <div className="text-sm">
                <div className="font-medium text-slate-700 dark:text-slate-300">
                  Ativo em operação
                </div>
                <div className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                  Desmarque se o ativo foi desativada fisicamente. O histórico
                  fica preservado, mas ela deixa de gerar alertas, insights AI
                  e some do dashboard operacional (aparece só com "Mostrar
                  arquivadas" ligado).
                </div>
              </div>
            </label>
          </div>
        )}
      </form>
    </Modal>
  );
}

// ---------- Tree split panel ------------------------------------------------

interface EquipmentsTreeSplitProps {
  sites: Site[];
  allEquipments: Equipment[];  // pra contar por site na sidebar
  filteredEquipments: Equipment[];  // já filtrado pelo nó selecionado
  deviceCountByEquipment: Map<string, number>;
  selected: SelectedSiteNode;
  onSelect: (node: SelectedSiteNode) => void;
  onEdit: (eq: Equipment) => void;
  onDelete: (eq: Equipment) => void;
  onCreate: (prefill: { site_id?: string } | null) => void;
}

function EquipmentsTreeSplit({
  sites, allEquipments, filteredEquipments, deviceCountByEquipment,
  selected, onSelect, onEdit, onDelete, onCreate,
}: EquipmentsTreeSplitProps) {
  const rightTitle = (() => {
    if (selected.kind === "all") return "Todos os ativos";
    const s = sites.find((x) => x.id === selected.id);
    return s?.name ?? "Site";
  })();

  const canCreateContextual = selected.kind === "site";
  const createLabel =
    selected.kind === "site" ? `Nova ativo em ${rightTitle}` : "Nova ativo";

  return (
    <div className="grid gap-4 lg:grid-cols-[280px_1fr]">
      <Card>
        <CardBody className="p-3">
          <SitesSidebar
            sites={sites}
            equipments={allEquipments}
            selected={selected}
            onSelect={onSelect}
          />
        </CardBody>
      </Card>

      <div className="min-w-0">
        <div className="mb-3 flex items-center justify-between gap-2">
          <div>
            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
              {rightTitle}
            </h2>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {filteredEquipments.length} ativo(s)
            </p>
          </div>
          {canCreateContextual && (
            <Button
              size="sm"
              icon={<Plus className="h-3.5 w-3.5" />}
              onClick={() => onCreate({ site_id: selected.id })}
            >
              {createLabel}
            </Button>
          )}
        </div>

        {filteredEquipments.length === 0 ? (
          <Card>
            <CardBody className="py-10 text-center text-sm text-slate-500 dark:text-slate-400">
              Nenhum ativo{" "}
              {selected.kind === "site" ? "neste site" : "cadastrada"}.
            </CardBody>
          </Card>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {filteredEquipments.map((eq) => (
              <EquipmentAdminCard
                key={eq.id}
                equipment={eq}
                devicesCount={deviceCountByEquipment.get(eq.id) ?? 0}
                onEdit={() => onEdit(eq)}
                onDelete={() => onDelete(eq)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
